import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SeoHead from '../../components/feature/SeoHead';

export default function AboutPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    name: 'ABOUT | New Age Cinema - EIJI HOSHINO',
    description: '映像作家 EIJI HOSHINOのプロフィール。大学で美術を学び、映画・MV・実験映像を手がける映像制作者。',
    url: `${siteUrl}/about`,
    inLanguage: 'ja',
    mainEntity: {
      '@type': 'Person',
      name: 'EIJI HOSHINO',
      jobTitle: '映像作家 / Filmmaker',
      description: '大学で美術を学び、専門学校で映像を勉強。在学中から自主制作映画の制作を開始し、独自の映像表現を追求。MV・映画・実験映画などジャンル問わず制作。脚本・撮影・編集にも携わる。',
      url: `${siteUrl}/about`,
      sameAs: [
        'https://www.youtube.com/channel/UC8JD2H3pNUagGOp6GsW_kKQ',
        'https://x.com/EIJIHOSHINO_NAC',
        'https://www.instagram.com/eiji_hoshino',
      ],
      knowsAbout: ['映画制作', 'MV制作', '実験映像', '脚本', '撮影', '編集'],
    },
  };

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="ABOUT | New Age Cinema - 映像作家 EIJI HOSHINO プロフィール"
        description="映像作家 EIJI HOSHINOのプロフィール。大学で美術を学び、映画・MV・実験映像を手がける映像制作者。脚本・撮影・編集まで一貫して制作。"
        keywords="EIJI HOSHINO,映像作家,プロフィール,映画制作,MV制作,実験映像,New Age Cinema"
        canonical="/about"
        jsonLd={jsonLd}
      />
      <Header />

      <main className="pt-24 md:pt-32 px-5 md:px-12 pb-20 md:pb-32">
        {/* Profile Section */}
        <div className="max-w-7xl mx-auto mb-20 md:mb-32">
          <div className="flex flex-col md:flex-row gap-10 md:gap-24">
            {/* Left: Photo */}
            <div className="w-full md:w-2/5">
              <div className="relative">
                <div className="aspect-square max-w-[280px] md:max-w-none mx-auto md:mx-0 overflow-hidden">
                  <img
                    src="https://static.readdy.ai/image/f493cbe5c2084134677121347fdba237/0f94e9376bd9ffbbc162af796e23f7c6.png"
                    alt="EIJI HOSHINO"
                    className="w-full h-full object-cover object-top grayscale"
                  />
                </div>
                <div className="absolute top-0 left-0 md:left-0 w-3 h-3 bg-[#00F0FF]"></div>
              </div>
              <div className="mt-6 md:mt-8 text-center md:text-left">
                <h1 className="text-xl md:text-2xl font-light tracking-[0.2em] text-black mb-2">EIJI HOSHINO</h1>
                <p className="text-sm font-light text-gray-500">映画作家 / Filmmaker</p>
              </div>
            </div>

            {/* Right: Bio */}
            <div className="w-full md:w-3/5 flex gap-4 md:gap-8">
              <h2 className="hidden md:block text-xs font-light tracking-[0.2em] text-gray-400" style={{ writingMode: 'vertical-rl' }}>
                ABOUT
              </h2>
              <div className="flex-1 max-w-2xl space-y-6 md:space-y-8">
                <div className="flex gap-3 md:gap-4">
                  <div className="w-1.5 h-1.5 bg-[#00F0FF] rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-base md:text-lg font-light text-black leading-relaxed">
                    大学で美術を学び、専門学校で映像を勉強。在学中から自主制作映画の制作を開始し、独自の映像表現を追求してきました。
                  </p>
                </div>
                <div className="flex gap-3 md:gap-4">
                  <div className="w-1.5 h-1.5 bg-[#00F0FF] rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-base md:text-lg font-light text-black leading-relaxed">
                    MV、映画、実験映画などジャンル問わず制作。それぞれの作品において、映像が持つ可能性を探求し、新しい表現方法を模索しています。
                  </p>
                </div>
                <div className="flex gap-3 md:gap-4">
                  <div className="w-1.5 h-1.5 bg-[#00F0FF] rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-base md:text-lg font-light text-black leading-relaxed">
                    脚本・撮影・編集にも携わり作品を制作し続けている。映像制作の全工程に関わることで、自身のビジョンを余すことなく表現することを目指しています。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Activities Section */}
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl md:text-4xl font-light tracking-[0.2em] text-center mb-10 md:mb-16">ACTIVITIES</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-12">
            <div className="border border-gray-200 p-8 md:p-12 hover:border-[#00F0FF] transition-all duration-300">
              <h3 className="text-base md:text-lg font-light tracking-[0.1em] text-black mb-4 md:mb-6">映画制作</h3>
              <p className="text-sm font-light text-gray-600 leading-relaxed">
                自主制作映画を中心に、物語性のある作品を制作。テーマは、見えないものの存在証明。人の魅力を映し出す映像を追求しています。
              </p>
            </div>
            <div className="border border-gray-200 p-8 md:p-12 hover:border-[#00F0FF] transition-all duration-300">
              <h3 className="text-base md:text-lg font-light tracking-[0.1em] text-black mb-4 md:mb-6">MV制作</h3>
              <p className="text-sm font-light text-gray-600 leading-relaxed">
                音楽と映像の融合を探求し、アーティストの世界観を視覚化。実験的な撮影手法や編集技術を用いて、独創的なミュージックビデオを制作しています。
              </p>
            </div>
            <div className="border border-gray-200 p-8 md:p-12 hover:border-[#00F0FF] transition-all duration-300">
              <h3 className="text-base md:text-lg font-light tracking-[0.1em] text-black mb-4 md:mb-6">実験映像</h3>
              <p className="text-sm font-light text-gray-600 leading-relaxed">
                既成概念にとらわれない映像表現に挑戦。時間、空間、記憶といった抽象的なテーマを、映像という媒体を通じて具現化する試みを続けています。
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}